// Modules
import React, { createContext, useContext, useState, useEffect } from 'react'

// Application
import { Contact } from 'typings'
import { Token, RecoverPasswordState, RecoverStep } from 'typings/recoverpassword'

const LIVIA_LS_KEY = '@LiviaRecoverPassword'

interface ContextData {
    // State
    state: {
        step: RecoverStep
        hasMounted: boolean
        isSubmitting: boolean
        username?: string
        token?: Token
        contact?: Contact
        contacts?: Contact[]
        phone?: string
        email?: string
        redirectTo?: string
        otpKey?: string
    }
    // Operations
    operations: {
        previousStep(): void
        nextStep(): void
        setStep(step: RecoverStep): void
        resetStep(): void
        setValidateToken(token: Token): Promise<void>
        setUsername(username: string): Promise<void>
        setContact(contact: Contact): Promise<void>
        setContacts(contact: Contact[]): Promise<void>
        setEmail(email: string): Promise<void>
        setPhone(phone: string): Promise<void>
        setSubmitting(value: boolean): Promise<void>
        setRedirectTo(value: string): Promise<void>
        resetRecoverPasswordState(): Promise<void>
        setOtpKey(otpKey: string): void
    }
}

const RecoverPasswordContext = createContext<ContextData>({} as ContextData)

export const RecoverPasswordProvider: React.FC = ({ children }) => {
    const [step, _setStep] = useState<RecoverStep>(RecoverStep.selectContact)
    const [hasMounted, _setMounted] = useState<boolean>(false)
    const [isSubmitting, _setSubmitting] = useState<boolean>(false)
    const [recoverPasswordData, setRecoverPasswordData] = useState<RecoverPasswordState | null>(null)
    const [redirectToUrl, setRedirectToUrl] = useState<string>()
    const [otpKey, _setOtpKey] = useState<string>()

    useEffect(() => {
        const username = sessionStorage.getItem(`${LIVIA_LS_KEY}:Username`)
        const contact = sessionStorage.getItem(`${LIVIA_LS_KEY}:Contact`)
        const contacts = sessionStorage.getItem(`${LIVIA_LS_KEY}:Contacts`)
        const phone = sessionStorage.getItem(`${LIVIA_LS_KEY}:PhoneContact`)
        const email = sessionStorage.getItem(`${LIVIA_LS_KEY}:EmailContact`)
        setRecoverPasswordData({
            username: username || undefined,
            contact: contact ? { ...JSON.parse(contact) } : {},
            contacts: contacts ? { ...JSON.parse(contacts) } : {},
            phone: phone || undefined,
            email: email || undefined,
        })
    }, [setRecoverPasswordData])

    useEffect(() => {
        if (recoverPasswordData) {
            _setMounted(true)
        }
    }, [recoverPasswordData])

    const setValidateToken = async (token: Token): Promise<void> => {
        setRecoverPasswordData({ ...recoverPasswordData, token })
    }

    const setContact = async (contact: Contact) => {
        setRecoverPasswordData(current => ({ ...current, contact }))
        sessionStorage.setItem(`${LIVIA_LS_KEY}:Contact`, JSON.stringify(contact))
    }

    const setUsername = async (username: string) => {
        setRecoverPasswordData(current => ({ ...current, username }))
        sessionStorage.setItem(`${LIVIA_LS_KEY}:Username`, JSON.stringify(username))
    }

    const setRedirectTo = async (url: string) => {
        setRedirectToUrl(url)
    }

    const resetRecoverPasswordState = async (): Promise<void> => {
        setRecoverPasswordData({
            username: undefined,
            contact: undefined,
            contacts: undefined,
            token: undefined,
            phone: undefined,
            email: undefined,
        })
        setRedirectToUrl(undefined)
    }

    const setEmail = async (email: string): Promise<void> => {
        setRecoverPasswordData(current => ({ ...current, email: email }))
    }

    const setContacts = async (contacts: Contact[]): Promise<void> => {
        setRecoverPasswordData(current => ({ ...current, contacts }))
        sessionStorage.setItem(`${LIVIA_LS_KEY}:Contact`, JSON.stringify(contacts))
    }

    const setPhone = async (phone: string): Promise<void> => {
        setRecoverPasswordData(current => ({ ...current, phone: phone }))
    }

    const setSubmitting = async (value: boolean): Promise<void> => {
        _setSubmitting(value)
    }

    const nextStep = (): void => {
        _setStep(value => value + 1)
    }

    const previousStep = (): void => {
        _setStep(value => value - 1)
    }

    const setStep = (value: RecoverStep): void => {
        _setStep(value)
    }

    const resetStep = (): void => {
        _setStep(0)
    }

    const setOtpKey = (OtpKey: string): void => {
        _setOtpKey(OtpKey)
    }

    return (
        <RecoverPasswordContext.Provider
            value={{
                state: {
                    step,
                    hasMounted,
                    isSubmitting,
                    redirectTo: redirectToUrl,
                    ...recoverPasswordData!,
                    otpKey,
                },
                operations: {
                    setStep,
                    nextStep,
                    previousStep,
                    resetStep,
                    setValidateToken,
                    setContacts,
                    setUsername,
                    setEmail,
                    setPhone,
                    setContact,
                    setSubmitting,
                    setRedirectTo,
                    resetRecoverPasswordState,
                    setOtpKey,
                },
            }}
        >
            {children}
        </RecoverPasswordContext.Provider>
    )
}

export function useRecoverContext(): ContextData {
    const context = useContext(RecoverPasswordContext)
    if (context === undefined) {
        throw new Error('useRecoverContext value is undefined. Make sure you use the RecoverProvider before using the context.')
    }
    return context
}
